import React, { useState, useCallback } from 'react';
import ImageUpload from './image-upload';
import { useImageUpload } from '@/hooks/useImageUpload';
import { Button } from './button';
import { Palette, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LogoUploadProps {
  onLogoChange: (url: string | null) => void;
  currentLogo?: string | null;
  organizationName?: string;
  disabled?: boolean;
  className?: string;
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  onLogoChange,
  currentLogo,
  organizationName = "Organization",
  disabled = false,
  className
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showPreviewMode, setShowPreviewMode] = useState(false);
  const { uploading, uploadProgress, uploadImage } = useImageUpload();

  const handleFileSelect = useCallback(async (file: File) => {
    setSelectedFile(file);
    
    // Upload immediately
    const result = await uploadImage(file, {
      bucket: 'app_images',
      maxWidth: 512,
      maxHeight: 512,
      quality: 0.9,
      generateThumbnail: true
    });

    if (result) {
      onLogoChange(result.url);
    }
  }, [uploadImage, onLogoChange]);

  const handleFileRemove = useCallback(() => {
    setSelectedFile(null);
    onLogoChange(null);
  }, [onLogoChange]);

  const currentImageSrc = selectedFile ? URL.createObjectURL(selectedFile) : currentLogo;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Upload Component */}
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <label className="text-sm font-medium">Organization Logo</label>
          {currentImageSrc && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowPreviewMode(!showPreviewMode)}
              className="w-full sm:w-auto"
            >
              {showPreviewMode ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {showPreviewMode ? 'Hide Preview' : 'Preview'}
            </Button>
          )}
        </div>
        
        <ImageUpload
          onImageSelect={handleFileSelect}
          onImageRemove={handleFileRemove}
          selectedImage={selectedFile || currentLogo}
          maxSize={5 * 1024 * 1024} // 5MB
          minDimensions={{ width: 200, height: 200 }}
          disabled={disabled || uploading}
          placeholder="Upload organization logo"
          accept={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/svg+xml': ['.svg'],
            'image/webp': ['.webp']
          }}
        />

        {uploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Logo Guidelines */}
      <div className="p-4 bg-muted/25 rounded-lg border border-muted">
        <div className="flex items-center gap-2 mb-2">
          <Palette className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Logo Guidelines</span>
        </div>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• Minimum size: 200×200 pixels for crisp display</li>
          <li>• Recommended: Square format (1:1 aspect ratio)</li>
          <li>• File formats: PNG (with transparency), JPG, SVG, or WebP</li>
          <li>• Maximum file size: 5MB</li>
          <li>• Works best with simple, readable designs</li>
        </ul>
      </div>

      {/* Preview Mode */}
      {showPreviewMode && currentImageSrc && (
        <div className="space-y-4 p-3 sm:p-4 bg-muted/10 rounded-lg border animate-fade-in">
          <h4 className="text-sm font-medium">Logo Preview</h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Header Preview */}
            <div className="space-y-2">
              <span className="text-xs text-muted-foreground">In navigation:</span>
              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-background border rounded-lg">
                <img 
                  src={currentImageSrc} 
                  alt="Logo preview" 
                  className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                />
                <span className="font-medium text-sm sm:text-base truncate">{organizationName}</span>
              </div>
            </div>

            {/* Card Preview */}
            <div className="space-y-2">
              <span className="text-xs text-muted-foreground">In cards:</span>
              <div className="p-3 sm:p-4 bg-background border rounded-lg text-center">
                <img 
                  src={currentImageSrc} 
                  alt="Logo preview" 
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain mx-auto mb-2"
                />
                <p className="text-sm font-medium truncate">{organizationName}</p>
                <p className="text-xs text-muted-foreground">Sample content</p>
              </div>
            </div>

            {/* Dark Mode Preview */}
            <div className="space-y-2 sm:col-span-2 lg:col-span-1">
              <span className="text-xs text-muted-foreground">Dark mode:</span>
              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-slate-900 text-white rounded-lg">
                <img 
                  src={currentImageSrc} 
                  alt="Logo preview dark" 
                  className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                />
                <span className="font-medium text-sm sm:text-base truncate">{organizationName}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoUpload;